import { Button, Field, Input, Notice } from '@didto/ui';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/app/session';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import { AuthFrame } from './AuthFrame';
import './auth.css';

/** A sign-in refusal in words for the owner: the two the server sends most, then the shared sentences. */
function signInError(err: { message: string; status?: number }): string {
  if (err.message === 'Invalid login credentials') return 'Email or password is incorrect';
  if (err.status === 429) return 'Too many sign-in attempts. Wait a minute and try again.';
  return errorText(err);
}

/** Owner / manager login: Supabase Auth email + password. TOTP step-up happens on /mfa. */
export function LoginPage() {
  const s = useSession();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: string; passwordChanged?: boolean } };
  // ADR-037: signing in needs the server; the connection banner above says why it is paused
  const online = useOnline();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (s.ready && s.authed && s.me?.principal === 'human')
    return <Navigate to={loc.state?.from ?? '/'} replace />;
  if (s.ready && s.authed && s.me?.principal === 'device')
    return <Navigate to={s.me.terminal?.landing ?? '/lock'} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (err) {
        setError(signInError(err));
        return;
      }
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.nextLevel === 'aal2' && aal.currentLevel !== 'aal2') {
        nav('/mfa', { replace: true, state: { from: loc.state?.from } });
        return;
      }
      await s.refresh();
      nav(loc.state?.from ?? '/', { replace: true });
    } catch (x) {
      setError(errorText(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title="Sign in" subtitle="Owner and manager access">
      <form onSubmit={(e) => void submit(e)} className="d-stack" noValidate>
        {loc.state?.passwordChanged && !error && (
          <Notice tone="success" role="status" title="Password changed" testId="login-notice">
            Sign in with your new password.
          </Notice>
        )}
        <Field label="Email">
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
            />
          )}
        </Field>
        <Field label="Password">
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
            />
          )}
        </Field>
        {error && (
          <Notice tone="danger" role="alert" testId="login-error">
            {error}
          </Notice>
        )}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          busy={busy}
          disabled={!online}
          data-testid="login-submit"
        >
          Sign in
        </Button>
        <p className="d-help signin__note">
          Café staff do not sign in here. <Link to="/enroll">Enrol a tablet</Link> or use the PIN
          screen on an enrolled device.
        </p>
      </form>
    </AuthFrame>
  );
}
